import PersonIcon from '@mui/icons-material/Person';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { Alert, Box, CircularProgress, Grid, Stack } from '@mui/material';
import BackendService from 'BackendService';
import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import AdminModelCard from '../../components/admin/AdminModelCard';
import FundOpportunityCard from '../../components/admin/FundOpportunityCard';
import { generateActionButton } from '../../components/styles/ModelCard';
import StyledSection from '../../components/styles/StyledSection';
import { StyledHeading, StyledTypography } from '../../components/styles/styles';
import Contact from '../../models/Contact';
import FundOpportunity from '../../models/FundOpportunity';

interface InviterInvestor {
  id: string;
  contactId: string;
  fundOpportunityId: string;
  status: string;
}

interface ReferralInvitationData {
  invitation: {
    id: string;
    inviterContactId: number;
    inviteeEmail: string;
    inviteeFirstName: string;
    inviteeLastName: string;
    invitationLink: string;
    invitationCode: string;
    invitationExpiry: string;
    status: string;
    rejectionReason: string | null;
    opportunityId: number | null;
    eoiAmount: number | null;
    inviteeContactId: number | null;
    createdAt: string;
    updatedAt: string;
    expired: boolean;
    expiresIn: number;
  };
  inviterContact: Contact;
  fundOpportunity: FundOpportunity | null;
  inviterInvestor: InviterInvestor | null;
}

const ReferralInvitationAdminPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<ReferralInvitationData | null>(null);

  useEffect(() => {
    if (!id) {
      setError('Invitation ID is required');
      setLoading(false);
      return;
    }

    const fetchInvitation = async () => {
      try {
        setLoading(true);
        setError(null);
        const backendService = new BackendService();
        const response = await backendService.getReferralInvitationDetails(id);
        setData(response.data);
      } catch (err) {
        console.error('Failed to fetch referral invitation:', err);
        setError(err instanceof Error ? err.message : 'Failed to load referral invitation details');
      } finally {
        setLoading(false);
      }
    };

    fetchInvitation();
  }, [id]);

  const handleContactClick = (contactId: string) => {
    navigate(`/contacts/${contactId}`);
  };

  const handleInvestorClick = (fundOpportunityId: string, investorId: string) => {
    navigate(`/opportunities/${fundOpportunityId}/investors/${investorId}`);
  };

  if (loading) {
    return (
      <StyledSection noSidePaddingFrom="xs" noTopPadding={true}>
        <StyledHeading sx={{ px: { xs: '1.875rem', lg: 0 } }}>Referral Invitation</StyledHeading>
        <StyledSection tone="tone1">
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress />
          </Box>
        </StyledSection>
      </StyledSection>
    );
  }

  if (error || !data) {
    return (
      <StyledSection noSidePaddingFrom="xs" noTopPadding={true}>
        <StyledHeading sx={{ px: { xs: '1.875rem', lg: 0 } }}>Referral Invitation</StyledHeading>
        <StyledSection tone="tone1">
          <Alert severity="error" sx={{ mb: 3 }}>
            {error || 'Failed to load referral invitation details'}
          </Alert>
        </StyledSection>
      </StyledSection>
    );
  }

  const { invitation, inviterContact, fundOpportunity, inviterInvestor } = data;

  return (
    <StyledSection noSidePaddingFrom="xs" noTopPadding={true}>
      <StyledHeading sx={{ px: { xs: '1.875rem', lg: 0 } }}>Referral Invitation</StyledHeading>
      <StyledSection tone="tone1">
        {error && (
          <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {/* Invitation Details */}
        <Box sx={{ mb: 4 }}>
          <StyledTypography variant="h6" sx={{ mb: 2, fontWeight: 600 }} tone="tone1">
            Invitation Details
          </StyledTypography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <StyledTypography variant="body2" tone="tone1" sx={{ mb: 1 }}>
                <strong>Status:</strong> {invitation.status}
              </StyledTypography>
              <StyledTypography variant="body2" tone="tone1" sx={{ mb: 1 }}>
                <strong>Invitee:</strong> {invitation.inviteeFirstName} {invitation.inviteeLastName}
              </StyledTypography>
              <StyledTypography variant="body2" tone="tone1" sx={{ mb: 1 }}>
                <strong>Invitee Email:</strong> {invitation.inviteeEmail}
              </StyledTypography>
              {invitation.eoiAmount && (
                <StyledTypography variant="body2" tone="tone1" sx={{ mb: 1 }}>
                  <strong>EOI Amount:</strong> ${invitation.eoiAmount.toLocaleString()}
                </StyledTypography>
              )}
            </Grid>
            <Grid item xs={12} md={6}>
              <StyledTypography variant="body2" tone="tone1" sx={{ mb: 1 }}>
                <strong>Created:</strong> {new Date(invitation.createdAt).toLocaleString()}
              </StyledTypography>
              <StyledTypography variant="body2" tone="tone1" sx={{ mb: 1 }}>
                <strong>Expires:</strong> {new Date(invitation.invitationExpiry).toLocaleString()}
              </StyledTypography>
              {invitation.expired && (
                <StyledTypography variant="body2" tone="tone1" sx={{ mb: 1, color: 'error.main' }}>
                  <strong>Expired</strong>
                </StyledTypography>
              )}
              {invitation.rejectionReason && (
                <StyledTypography variant="body2" tone="tone1" sx={{ mb: 1 }}>
                  <strong>Rejection Reason:</strong> {invitation.rejectionReason}
                </StyledTypography>
              )}
            </Grid>
          </Grid>
        </Box>

        {/* Inviter Contact Card */}
        <Box sx={{ mb: 4 }}>
          <StyledTypography variant="h6" sx={{ mb: 2, fontWeight: 600 }} tone="tone1">
            Referred by
          </StyledTypography>
          <Box
            sx={{
              minWidth: '300px',
              maxWidth: '400px',
            }}
          >
            <AdminModelCard
              name={
                `${inviterContact.firstName || ''} ${inviterContact.lastName || ''}`.trim() ||
                'Unnamed Contact'
              }
              email={inviterContact.email || ''}
              phone={inviterContact.mobile || ''}
              entity={inviterContact}
              tone="tone2"
              contactId={inviterContact.id}
              actionButtons={
                <Stack direction="row" spacing={0.5}>
                  {generateActionButton('View Contact', <PersonIcon />, () =>
                    handleContactClick(inviterContact.id),
                  )}
                  {inviterInvestor &&
                    generateActionButton('View Investor', <VisibilityIcon />, () =>
                      handleInvestorClick(inviterInvestor.fundOpportunityId, inviterInvestor.id),
                    )}
                </Stack>
              }
              hideViewButtons
              allowWrap={true}
            />
          </Box>
        </Box>

        {/* Fund Opportunity Card */}
        {fundOpportunity && (
          <Box sx={{ mb: 4 }}>
            <StyledTypography variant="h6" sx={{ mb: 2, fontWeight: 600 }} tone="tone1">
              Fund Shared
            </StyledTypography>
            <Box
              sx={{
                minWidth: '300px',
                maxWidth: '400px',
              }}
            >
              <FundOpportunityCard
                opportunity={fundOpportunity as FundOpportunity & { investor?: any }}
                tone="tone2"
                compact={false}
              />
            </Box>
          </Box>
        )}
      </StyledSection>
    </StyledSection>
  );
};

export default ReferralInvitationAdminPage;
